/**
 * Redimensionado de imágenes en el navegador, antes de subirlas a Storage.
 *
 * Por qué existe: `{products|accessories}/{code}/thumbnail` NO era un thumbnail. El form
 * lo arma copiando la ruta de la primera imagen (ver `onClickSave` en FormBoxGameComponent),
 * así que terminaba siendo la imagen ORIGINAL con otro nombre — medido en el bucket de
 * producción: 1600x1600 y 375 KB para renderizarse en una tarjeta de ~320px. El catálogo
 * llegó a pesar 1.2 MB por página, y de eso 656 KB eran tres imágenes.
 *
 * Reglas:
 *  - NUNCA agranda: el lado mayor del resultado es, como mucho, el del original. Agrandar
 *    solo produce una imagen borrosa y más pesada; preferimos servir la nativa y que se
 *    reemplace el archivo de origen.
 *  - Pero "no agrandar" NO es "no tocar": una imagen que ya entra en el objetivo igual se
 *    re-encoda a WebP, porque el objetivo también es que PESE menos. Antes se cortaba acá
 *    con un `return blob` y un JPEG de 800x800 y 155 KB se subía intacto — o sea que el
 *    thumbnail quedaba byte a byte igual al original y volvía el bug que esto vino a
 *    arreglar. Medido en prod el 2026-08-26: 7 de los 20 productos creados desde que existe
 *    esta función salieron así, todos con la foto de origen en 539-800 px.
 *  - Preserva la relación de aspecto y limita el LADO MAYOR, no el ancho: así funciona
 *    igual con imágenes cuadradas, apaisadas y verticales.
 *  - Sale en WebP, con fallback automático a JPEG si el navegador no lo soporta.
 */

/** Lado mayor del thumbnail. La tarjeta del catálogo mide ~320x427 CSS, o sea ~855px en retina. */
export const THUMBNAIL_MAX_PX = 800;
const THUMBNAIL_QUALITY = 0.82;

const cargarImagen = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen')); };
    img.src = url;
  });

const canvasABlob = (canvas, tipo, calidad) =>
  new Promise((resolve) => canvas.toBlob((b) => resolve(b), tipo, calidad));

/**
 * Devuelve una versión redimensionada del blob, o el blob ORIGINAL si no hace falta tocarlo.
 * Nunca lanza: ante cualquier problema devuelve el original, porque subir la imagen grande
 * es mucho mejor que no subir nada.
 *
 * @param {Blob} blob
 * @param {number} maxPx  lado mayor máximo
 * @param {number} calidad  0..1
 * @returns {Promise<Blob>}
 */
export const resizeImageBlob = async (blob, maxPx = THUMBNAIL_MAX_PX, calidad = THUMBNAIL_QUALITY) => {
  try {
    if (!blob || !blob.type || !blob.type.startsWith('image/')) return blob;
    // Los SVG no se rasterizan: no tiene sentido y perderían la escalabilidad.
    if (blob.type === 'image/svg+xml') return blob;

    const img = await cargarImagen(blob);
    const ladoMayor = Math.max(img.naturalWidth, img.naturalHeight);

    // No agrandar: si ya entra en el objetivo se re-encoda en su tamaño nativo (escala 1),
    // no se corta acá. Lo que se gana en ese caso es peso, no píxeles.
    const escala = ladoMayor > maxPx ? maxPx / ladoMayor : 1;
    const yaEntraEnElObjetivo = escala === 1;

    const w = Math.round(img.naturalWidth * escala);
    const h = Math.round(img.naturalHeight * escala);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);

    // toBlob devuelve null (o un PNG) si el tipo no está soportado: ahí caemos a JPEG.
    let salida = await canvasABlob(canvas, 'image/webp', calidad);
    if (!salida || salida.type !== 'image/webp') {
      // Sin WebP y habiendo que achicar, JPEG sigue siendo mucho mejor que subir el original
      // entero. Pero si la imagen YA entraba, lo único en juego eran unos KB, y no vale
      // pagarlos con el canal alfa de un PNG transparente que hasta recién pasaba intacto.
      if (yaEntraEnElObjetivo) return blob;
      salida = await canvasABlob(canvas, 'image/jpeg', calidad);
    }
    if (!salida) return blob;

    // Si el "optimizado" pesa más que el original, no ganamos nada: nos quedamos con el original.
    return salida.size < blob.size ? salida : blob;
  } catch (e) {
    console.warn('[imageResize] no se pudo redimensionar, se sube el original:', e);
    return blob;
  }
};

/** Calidad de los derivados de portada. Igual que el thumbnail: es el punto donde WebP deja de notarse. */
const COVER_QUALITY = 0.82;

/**
 * Recorta y escala el blob a un tamaño EXACTO, con la misma regla que `object-fit: cover`
 * del navegador: agranda o achica hasta tapar el destino y descarta el sobrante.
 *
 * `focusX`/`focusY` (0..1) son el equivalente de `object-position`: deciden qué parte
 * sobrevive al recorte. 0.5/0.5 recorta parejo a los dos lados; 0.5/0 conserva el borde
 * de arriba. Los previews de la pantalla de portada usan literalmente
 * `object-position: ${focusX*100}% ${focusY*100}%`, así que lo que se ve es lo que sale.
 *
 * A diferencia de `resizeImageBlob` acá el destino tiene relación de aspecto propia (el hero
 * es casi cuadrado en celular y una franja en desktop), pero se mantiene la regla de la casa:
 * NUNCA agranda. Si el original no da para el tamaño pedido se devuelve el mismo recorte en
 * la resolución que el original soporta — más chico y nítido antes que estirado y borroso.
 *
 * Sí lanza si la imagen no se puede leer: acá no hay "subir el original" que valga, un archivo
 * con la relación de aspecto equivocada rompe el hero que venimos a arreglar.
 *
 * @param {Blob} blob
 * @param {{width:number, height:number, focusX?:number, focusY?:number, calidad?:number}} opciones
 * @returns {Promise<{blob: Blob, width: number, height: number, limitadoPorElOriginal: boolean}>}
 *   `limitadoPorElOriginal` = el original no daba para el tamaño pedido y salió más chico.
 */
export const renderCoverBlob = async (blob, opciones) => {
  const { width, height, focusX = 0.5, focusY = 0.5, calidad = COVER_QUALITY } = opciones;

  const img = await cargarImagen(blob);
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) throw new Error('La imagen no tiene dimensiones legibles');

  // Cuánto hay que escalar el original para tapar el destino.
  const escala = Math.max(width / iw, height / ih);

  // No agrandar: si no alcanza, se achica el destino manteniendo su relación de aspecto.
  // El encuadre no cambia, solo la resolución del archivo resultante.
  const destW = escala > 1 ? Math.max(1, Math.round(width / escala)) : width;
  const destH = escala > 1 ? Math.max(1, Math.round(height / escala)) : height;

  const escalaFinal = Math.max(destW / iw, destH / ih);
  const dibujoW = iw * escalaFinal;
  const dibujoH = ih * escalaFinal;

  // El sobrante se reparte según el punto focal, igual que object-position.
  const dx = -(dibujoW - destW) * focusX;
  const dy = -(dibujoH - destH) * focusY;

  const canvas = document.createElement('canvas');
  canvas.width = destW;
  canvas.height = destH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, dibujoW, dibujoH);

  // toBlob devuelve null (o un PNG) si el tipo no está soportado: ahí caemos a JPEG.
  let salida = await canvasABlob(canvas, 'image/webp', calidad);
  if (!salida || salida.type !== 'image/webp') {
    salida = await canvasABlob(canvas, 'image/jpeg', calidad);
  }
  if (!salida) throw new Error('El navegador no pudo generar la imagen');

  return { blob: salida, width: destW, height: destH, limitadoPorElOriginal: escala > 1 };
};
